import { useMemo, useState } from "react";
import { IconCash } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Chip, StatusChip } from "../../../../components/common/chip";
import { SimpleTable } from "../../../../components/common/simple-table";
import type { SimpleTableColumn } from "../../../../components/common/simple-table";
import { Pagination } from "../../../../components/common/pagination";
import { AccentStatCard } from "../../../../components/common/accent-stat-card";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { EmptyState } from "../../../../components/common/empty-state";
import {
  useMyPayments,
  useMyUnpaidBills,
  type VendorBillDto,
} from "../../hooks/useMyPayments";
import { usePendingRelease } from "../../../seller-dashboard/hooks/useSellerDashboard";
import {
  PaymentDtoStateEnum,
  type PaymentDto,
} from "../../../../api";
import { dollarFormat } from "../../../../utils/currencies";

const PAGE_SIZE = 14;

interface PaymentRow {
  id: string;
  orderId: string;
  paymentId: string | null;
  amount: number;
  currency: string;
  paymentDate: string | null;
  paymentMethod: string | null;
  // Delivery status doesn't come from the payment record directly — UI fills "—".
  // Once BE embeds origin PO state on PaymentDto/VendorBillDto we can colour it.
  deliveryStatus: "delivered" | null;
  status: "paid" | "unpaid";
}

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
});

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : DATE_FMT.format(d);
}

function paymentToRow(p: PaymentDto): PaymentRow {
  return {
    id: `pay-${p.id}`,
    orderId: p.originRef ?? p.reference ?? `Payment #${p.id}`,
    paymentId: p.reference ?? String(p.id),
    amount: p.amount ?? 0,
    currency: p.currency ?? "QAR",
    paymentDate: p.date ?? null,
    paymentMethod: p.journalName,
    deliveryStatus: "delivered",
    status: "paid",
  };
}

function billToRow(b: VendorBillDto): PaymentRow {
  return {
    id: `bill-${b.id}`,
    orderId: b.originRef ?? b.reference ?? `Bill #${b.id}`,
    paymentId: null,
    amount: b.amountResidual ?? 0,
    currency: b.currency ?? "QAR",
    paymentDate: null,
    paymentMethod: null,
    deliveryStatus: null,
    status: "unpaid",
  };
}

export function SellerPaymentsPage() {
  const [page, setPage] = useState(1);

  // Backend caps list pages at 100, so we stay within the limit here.
  // If this page needs exact totals beyond 100 records, it will need a
  // dedicated summary endpoint or multi-page aggregation.
  const payments = useMyPayments({ page: 1, pageSize: 100 });
  const unpaid = useMyUnpaidBills({ page: 1, pageSize: 100 });
  const summary = usePendingRelease();

  const rows = useMemo<PaymentRow[]>(() => {
    const fromPayments = (payments.data?.data ?? [])
      .filter((p) => p.state === PaymentDtoStateEnum.Paid)
      .map(paymentToRow);
    const fromBills = (unpaid.data?.data ?? []).map(billToRow);
    // Merge and sort: paid by date desc, unpaid by due date asc at the bottom.
    return [...fromPayments, ...fromBills].sort((a, b) => {
      if (a.status !== b.status) return a.status === "paid" ? -1 : 1;
      const at = a.paymentDate ? new Date(a.paymentDate).getTime() : 0;
      const bt = b.paymentDate ? new Date(b.paymentDate).getTime() : 0;
      return bt - at;
    });
  }, [payments.data, unpaid.data]);

  const isLoading =
    payments.isLoading || unpaid.isLoading || summary.isLoading;

  const totalEarned = useMemo(
    () =>
      (payments.data?.data ?? [])
        .filter((p) => p.state === PaymentDtoStateEnum.Paid)
        .reduce((s, p) => s + (p.amount ?? 0), 0),
    [payments.data],
  );

  // "Released" — payments paid in the last 30 days. Different from `totalEarned`
  // (cumulative). Backend could return this directly via an
  // `/payments/me/earnings-summary` endpoint to avoid pulling all payments
  // client-side; for now we compute on the page.
  const released = useMemo(() => {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return (payments.data?.data ?? [])
      .filter(
        (p) =>
          p.state === PaymentDtoStateEnum.Paid &&
          p.date &&
          new Date(p.date).getTime() >= cutoff,
      )
      .reduce((s, p) => s + (p.amount ?? 0), 0);
  }, [payments.data]);

  const currency = summary.data?.currency ?? "QAR";
  const pendingTotal = summary.data?.totalAmount ?? 0;

  const columns: SimpleTableColumn<PaymentRow>[] = [
    {
      key: "orderId",
      label: "Orders",
      className: "flex-[2]",
      render: (r) => (
        <Text variant="body" tone="dark">
          {r.orderId}
        </Text>
      ),
    },
    {
      key: "paymentId",
      label: "Payment ID",
      className: "flex-[1.5]",
      render: (r) => (
        <Text variant="body" tone="body">
          {r.paymentId ?? "—"}
        </Text>
      ),
    },
    {
      key: "amount",
      label: `Amount (${currency})`,
      className: "flex-1",
      render: (r) => (
        <Text variant="body" tone="dark">
          {r.amount.toLocaleString()}
        </Text>
      ),
    },
    {
      key: "paymentDate",
      label: "Payment Date",
      className: "flex-[1.2]",
      render: (r) => (
        <Text variant="body" tone="body">
          {formatDate(r.paymentDate) ?? "—"}
        </Text>
      ),
    },
    {
      key: "paymentMethod",
      label: "Payment Method",
      className: "flex-[1.2]",
      render: (r) => (
        <Text variant="body" tone="body">
          {r.paymentMethod ?? "—"}
        </Text>
      ),
    },
    {
      key: "deliveryStatus",
      label: "Delivery Status",
      className: "flex-1 flex justify-center",
      render: (r) =>
        r.deliveryStatus ? (
          <StatusChip status={r.deliveryStatus} />
        ) : (
          <Text variant="bodySm" tone="muted">
            —
          </Text>
        ),
    },
    {
      key: "status",
      label: "Status",
      className: "flex-[0.8] flex justify-center",
      render: (r) =>
        r.status === "paid" ? (
          <Chip variant="approved">Paid</Chip>
        ) : (
          <Chip variant="tertiary">Unpaid</Chip>
        ),
    },
  ];

  const pageCount = Math.ceil(rows.length / PAGE_SIZE);
  const paged = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:px-4 max-md:py-6">
      <Heading variant="h2" tone="dark">
        Payments
      </Heading>

      <div className="flex gap-4 max-md:flex-col max-md:gap-3">
        <AccentStatCard
          bare
          label="Total earned"
          value={dollarFormat(totalEarned, currency)}
          className="flex-1"
        />
        <AccentStatCard
          bare
          label="Pending release"
          value={dollarFormat(pendingTotal, currency)}
          className="flex-1"
        />
        <AccentStatCard
          bare
          label="Released (30d)"
          value={dollarFormat(released, currency)}
          className="flex-1"
        />
      </div>

      {isLoading ? (
        <ShimmerLoader message="Fetching your payments…" lines={6} />
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-borderDefault bg-bgWhite">
          <EmptyState
            icon={IconCash}
            title="No payments yet"
            description="Once your orders are paid out, you'll see settled and pending payments here."
          />
        </div>
      ) : (
        <SimpleTable
          columns={columns}
          data={paged}
          keyExtractor={(r) => r.id}
          footer={
            pageCount > 1 ? (
              <div className="flex justify-center">
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  onPageChange={setPage}
                />
              </div>
            ) : undefined
          }
        />
      )}
    </div>
  );
}

export default SellerPaymentsPage;
